import { isValidElement } from 'react';
import type { ReactNode } from 'react';
import type { ColumnDef } from './types';

export interface DataGridBodyProps<T> {
  columns: ColumnDef<T>[];
  data: T[];
  selectable?: boolean;
  selectedRows: Set<string>;
  getRowId: (row: T, index: number) => string;
  onToggleRow?: (rowId: string, checked: boolean) => void;
  onRowClick?: (row: T) => void;
}

function getCellValue<T>(row: T, accessor: ColumnDef<T>['accessor']): unknown {
  if (typeof accessor === 'function') {
    return accessor(row);
  }

  return row[accessor];
}

function renderCellValue(value: unknown): ReactNode {
  if (isValidElement(value)) return value;
  if (value === null || value === undefined || value === '') return <span className="text-gray-400">—</span>;
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') return String(value);
  if (value instanceof Date) return value.toISOString();

  return JSON.stringify(value);
}

export function DataGridBody<T>({
  columns,
  data,
  selectable = false,
  selectedRows,
  getRowId,
  onToggleRow,
  onRowClick,
}: DataGridBodyProps<T>) {
  const visibleColumns = columns.filter((column) => !column.hidden);

  return (
    <tbody className="bg-white dark:bg-gray-900">
      {data.map((row, index) => {
        const rowId = getRowId(row, index);
        const isSelected = selectedRows.has(rowId);

        return (
          <tr
            key={rowId}
            className={`border-b border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800/50 even:bg-gray-50/30 dark:even:bg-gray-800/20 ${
              isSelected ? 'bg-primary-50 dark:bg-primary-900/20' : ''
            } ${onRowClick ? 'cursor-pointer' : ''}`}
            onClick={onRowClick ? () => onRowClick(row) : undefined}
          >
            {selectable && (
              <td className="px-4 py-3 w-12" onClick={(event) => event.stopPropagation()}>
                <input
                  type="checkbox"
                  checked={isSelected}
                  onChange={(event) => onToggleRow?.(rowId, event.target.checked)}
                  className="h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-primary-700 bg-white dark:bg-gray-900 focus:ring-2 focus:ring-primary-500 focus:outline-none"
                  aria-label={`Select row ${index + 1}`}
                />
              </td>
            )}
            {visibleColumns.map((column) => {
              const value = getCellValue(row, column.accessor);
              const content = column.render ? column.render(value, row) : renderCellValue(value);

              return (
                <td
                  key={column.id}
                  className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100 align-top"
                  style={{ width: column.width, minWidth: column.minWidth }}
                >
                  {content}
                </td>
              );
            })}
          </tr>
        );
      })}
    </tbody>
  );
}
